import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';

import TopModalSection from '../common/TopModalSection';

import ProductForm from '@/components/forms/product/ProductForm';
import Button from '@/components/ui/button';
import Loader from '@/components/ui/loader/Loader';
import { formatDateTime, formatPrice } from '@/components/utils/utils';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { ICreateProductPayload } from '@/interfaces/api/product/ICreateProductPayload';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { haggleApiService } from '@/services/haggle.service';

type Props = {
	closeModal: () => void;
	productId: number | null;
	refetchProducts: () => void;
};

const ProductViewModal = ({
	closeModal,
	productId,
	refetchProducts,
}: Props) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [loading, setLoading] = useState(true);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);

	const { user } = useUser();

	const {
		data: product,
		isLoading: productIsLoading,
		error: productError,
	} = useQuery<IProduct>({
		queryKey: [ApiQueryKeys.getProductById, productId],
		queryFn: () => haggleApiService.getProductById(productId!.toString()),
		enabled: !!productId,
		refetchOnWindowFocus: false,
	});

	// Delete mutation
	const deleteProductMutation = useMutation({
		mutationFn: (productId: number) =>
			haggleApiService.deleteProduct(productId),
		onSuccess: () => {
			refetchProducts();
			closeModal();
		},
	});

	// Update mutation
	const updateProductMutation = useMutation({
		mutationFn: ({
			productId,
			values,
		}: {
			productId: number;
			values: ICreateProductPayload;
		}) => haggleApiService.updateProduct(productId, values),
		onSuccess: () => {
			refetchProducts();
		},
	});

	// Convert product data to form format
	const convertProductToFormData = (
		product: IProduct,
	): ICreateProductPayload => {
		return {
			handle: product.handle || '',
			title: product.title || '',
			bodyHtml: product.body_html || '',
			productInfo: product.product_info || null,
			// vendor: product.vendor || '',
			type: product.product_type || '',
			tags: Array.isArray(product.tags)
				? product.tags.join(', ')
				: product.tags || '',
			published: product.status === 'active',
			imageSrc: product.image?.src || '',
			variants:
				product.variants?.map((variant) => ({
					id: variant.id, // Include the variant ID for updating existing variants
					handle: product.handle || '',
					type: variant.type || variant.title || '',
					name: variant.title || '',
					sku: variant.sku || '',
					grams: variant.grams || 0,
					inventoryQty: variant.inventory_quantity || 0,
					price: parseFloat(variant.price) || 0,
					requiresShipping: variant.requires_shipping || false,
					barcode: variant.barcode || '',
					image: product.images.find((img) => img.id === variant.id)?.src || '',
					weightUnit: 'kg',
				})) || [],
		};
	};

	// Handle functions first
	const handleUpdateProduct = async (values: ICreateProductPayload) => {
		if (!product?.id) return;
		updateProductMutation.mutate({ productId: product.id, values });
		toast.success('Product updated successfully!');
	};

	// Combine product.image and product.images into one array, avoiding duplicates
	const allImages = (() => {
		if (!product) return [];
		const imagesArr = Array.isArray(product.images) ? product.images : [];
		const uniqueImages = imagesArr.filter(
			(img, index, self) => index === self.findIndex((t) => t.src === img.src),
		);
		if (product.image) {
			const isDuplicate = imagesArr.some(
				(img) => img.src === product.image.src,
			);
			return isDuplicate ? uniqueImages : [product.image, ...uniqueImages];
		}
		return uniqueImages;
	})();

	const handleImageLoad = () => {
		setLoading(false);
	};

	const handleImageClick = (index: number) => {
		if (allImages[index]) {
			const imgElement = new window.Image();
			imgElement.src = allImages[index].src;
			if (!imgElement.complete) {
				setLoading(true);
			}
			setCurrentIndex(index);
		}
	};

	const nextImage = () => {
		if (allImages.length > 0) {
			handleImageClick((currentIndex + 1) % allImages.length);
		}
	};

	const prevImage = () => {
		if (allImages.length > 0) {
			handleImageClick(
				(currentIndex - 1 + allImages.length) % allImages.length,
			);
		}
	};

	const handleDeleteProduct = () => {
		if (!product?.id) return;
		deleteProductMutation.mutate(product.id);
	};

	// Modal state - defined after the functions it depends on

	const openEditModal = () => setEditModalOpen(true);
	const closeEditModal = () => {
		setEditModalOpen(false);
	};

	const handleEditSubmit = async (values: ICreateProductPayload) => {
		await handleUpdateProduct(values);
		closeEditModal();
		closeModal();
	};

	if (!productId) {
		return null;
	}

	if (productIsLoading) {
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
				<div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-2 my-8 p-0 flex flex-col">
					<TopModalSection
						className="gap-x-4 border-b py-2 w-full px-4 bg-gray-50 rounded-t-2xl"
						title="Loading Product..."
						close={closeModal}
					/>
					<div className="w-full p-6 flex justify-center items-center min-h-[400px]">
						<Loader />
					</div>
				</div>
			</div>
		);
	}

	if (productError || !product) {
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
				<div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-2 my-8 p-0 flex flex-col">
					<TopModalSection
						className="gap-x-4 border-b py-2 w-full px-4 bg-gray-50 rounded-t-2xl"
						title="Error"
						close={closeModal}
					/>
					<div className="w-full p-6 flex justify-center items-center min-h-[400px]">
						<div className="text-center">
							<p className="text-red-600 mb-4">
								Failed to load product details
							</p>
							<Button onClick={closeModal} size="mini" color="info">
								Close
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 overflow-y-auto">
			<div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-2 my-8 p-0 flex flex-col">
				<TopModalSection
					className="gap-x-4 border-b py-2 w-full px-4 bg-gray-50 rounded-t-2xl"
					title={product?.title || 'Shopify Product'}
					close={closeModal}
				/>
				<div className="w-full p-6 flex flex-col gap-6">
					{/* Product Images & Thumbnails */}
					<div className="flex flex-col md:flex-row gap-6">
						<div className="relative flex flex-col items-center w-full md:w-1/2">
							<div className="relative w-full flex justify-center items-center">
								{allImages.length ? (
									<>
										{loading && (
											<div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10 rounded-xl">
												<Loader />
											</div>
										)}
										<img
											src={allImages[currentIndex]?.src}
											alt={
												allImages[currentIndex]?.alt ??
												`${product?.title} image`
											}
											className="w-full h-72 object-contain rounded-xl border shadow-md"
											onLoad={handleImageLoad}
										/>
										{/* Image count badge */}
										<span className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full">
											{currentIndex + 1} / {allImages.length}
										</span>
										{/* Prev/Next Arrows */}
										<button
											className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-400"
											aria-label="Previous image"
											onClick={prevImage}
										>
											<FiChevronLeft />
										</button>
										<button
											className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-400"
											aria-label="Next image"
											onClick={nextImage}
										>
											<FiChevronRight />
										</button>
									</>
								) : (
									<div className="w-full h-72 flex items-center justify-center bg-gray-100 rounded-xl text-gray-400">
										No images
									</div>
								)}
							</div>
							{/* Thumbnails */}
							{allImages.length > 1 && (
								<div className="relative w-full flex items-center mt-4">
									<div
										className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 w-full pr-8"
										style={{ scrollBehavior: 'smooth' }}
									>
										{allImages.map((img, idx) => (
											<button
												key={img.id || img.src || idx}
												className={`w-14 h-14 flex-shrink-0 rounded-lg border-2 ${
													currentIndex === idx
														? 'border-blue-500'
														: 'border-gray-200'
												}`}
												aria-label={`Show image ${idx + 1}`}
												onClick={() => handleImageClick(idx)}
											>
												<img
													src={img.src}
													alt={img.alt ?? `Thumbnail ${idx + 1}`}
													className="w-full h-full object-contain rounded-lg"
												/>
											</button>
										))}
									</div>
								</div>
							)}
						</div>
						{/* Product Details */}
						<div className="flex-1 flex flex-col gap-4">
							<span
								className="text-2xl font-bold text-gray-900"
								data-test="product-title"
							>
								{product?.title}
							</span>
							{product?.product_type && (
								<span className="bg-purpleBrand-100 text-purpleBrand-700 text-xs font-semibold px-2 py-1 rounded w-fit">
									{product.product_type}
								</span>
							)}
							<div className="flex flex-wrap  items-center gap-2 mb-2">
								{product?.tags &&
									Array.isArray(product?.tags) &&
									product.tags.filter(Boolean).map((tag) => (
										<span
											key={tag}
											className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded"
										>
											{tag}
										</span>
									))}
							</div>
							{product?.body_html && (
								<div
									className="prose prose-sm max-h-32 overflow-y-auto text-gray-700"
									dangerouslySetInnerHTML={{ __html: product.body_html }}
								/>
							)}
							<div className="flex flex-col gap-4 text-xs text-gray-500">
								{product?.status && (
									<span>
										Status:{' '}
										<span className="font-semibold text-gray-700 bg-blue-100 px-2 py-1 rounded-md">
											{product.status}
										</span>
									</span>
								)}
								{product?.created_at && (
									<span>
										Created:{' '}
										<span className="font-semibold text-gray-700">
											{formatDateTime(product.created_at)}
										</span>
									</span>
								)}
								{product?.updated_at && (
									<span>
										Updated:{' '}
										<span className="font-semibold text-gray-700">
											{formatDateTime(product.updated_at)}
										</span>
									</span>
								)}
							</div>
						</div>
					</div>
					{/* Variants Table */}
					{product && product.variants && product.variants.length > 0 && (
						<div className="">
							<div className="font-semibold text-lg mb-2">Variants</div>
							<div className="overflow-x-auto rounded-lg border max-h-48 overflow-y-auto">
								<table className="min-w-full text-xs text-left">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-3 py-2 font-semibold">Title</th>
											<th className="px-3 py-2 font-semibold">Options</th>
											<th className="px-3 py-2 font-semibold">Price</th>
											<th className="px-3 py-2 font-semibold">Stock</th>
											<th className="px-3 py-2 font-semibold">SKU</th>
											<th className="px-3 py-2 font-semibold">Barcode</th>
										</tr>
									</thead>
									<tbody>
										{product.variants.map((variant) => (
											<tr key={variant.id} className="even:bg-gray-50">
												<td className="px-3 py-2">{variant.type}</td>
												<td className="px-3 py-2">{variant.title}</td>
												<td className="px-3 py-2">
													{formatPrice(variant.price)}
												</td>
												<td className="px-3 py-2">
													{variant.inventory_quantity}
												</td>
												<td className="px-3 py-2">{variant.sku}</td>
												<td className="px-3 py-2">{variant.barcode}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
					{user?.inventoryType === InventoryType.CSV && !product?.haggle && (
						<>
							<div className="flex justify-end items-center gap-2">
								<Button
									onClick={openEditModal}
									size="mini"
									color="info"
									className="px-4 py-2 text-sm text-white rounded-lg flex items-center gap-2"
									aria-label="Edit product"
								>
									Edit
								</Button>
								<Button
									onClick={() => setShowConfirmDelete(true)}
									size="mini"
									color="danger"
									className="px-4 py-2 text-sm text-white rounded-lg flex items-center gap-2"
									aria-label="Delete product"
								>
									Delete
								</Button>
							</div>

							{/* Confirmation Delete Dialog */}
							{showConfirmDelete && (
								<div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50">
									<div
										role="dialog"
										aria-modal="true"
										className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm flex flex-col gap-4"
									>
										<span className="text-lg font-semibold text-gray-900">
											Delete Product
										</span>
										<span className="text-gray-700">
											Are you sure you want to delete this product? This action
											cannot be undone.
										</span>
										{deleteProductMutation.error && (
											<span className="text-red-600 text-sm">
												{deleteProductMutation.error.message ||
													'Error deleting product'}
											</span>
										)}
										<div className="flex justify-end gap-2 mt-2">
											<Button
												onClick={() => setShowConfirmDelete(false)}
												size="mini"
												color="info"
												className="px-4 py-2 text-sm rounded-lg"
												aria-label="Cancel delete"
												disabled={deleteProductMutation.isPending}
											>
												Cancel
											</Button>
											<Button
												onClick={handleDeleteProduct}
												size="mini"
												color="danger"
												className="px-4 py-2 text-sm rounded-lg"
												aria-label="Confirm delete"
												disabled={deleteProductMutation.isPending}
											>
												{deleteProductMutation.isPending
													? 'Deleting...'
													: 'Delete'}
											</Button>
										</div>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* Edit Product Modal */}
			{editModalOpen && (
				<div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50">
					<div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-2 my-8 p-0 flex flex-col max-h-[90vh] overflow-y-auto">
						<TopModalSection
							className="gap-x-4 border-b py-2 w-full px-4 bg-gray-50 rounded-t-2xl sticky top-0 z-10"
							title="Edit Product"
							close={closeEditModal}
						/>
						<ProductForm
							handleSubmit={handleEditSubmit}
							loading={updateProductMutation.isPending}
							initialValues={
								product ? convertProductToFormData(product) : undefined
							}
							setIsLoading={setLoading}
							closeModal={closeEditModal}
							isEditing={true}
						/>
					</div>
				</div>
			)}
		</div>
	);
};
export default ProductViewModal;
